import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Seo, { breadcrumbLd } from '../components/Seo';
import { MaskedLines, FadeUp } from '../components/motion';
import { useT } from '../i18n/LangContext';
import { useContent } from '../data/useContent';

export default function GalleryPage() {
  const t = useT();
  const { TILES, GALLERY_EXTRA } = useContent();
  const ALL = [...TILES, ...GALLERY_EXTRA];
  const FILTERS = [
    { v: 'all', l: t['gl.all'] },
    { v: 'interior', l: t['gl.interior'] },
    { v: 'exterior', l: t['gl.exterior'] },
  ];
  const [params] = useSearchParams();
  const [type, setType] = useState(params.get('type') || 'all');
  const [space, setSpace] = useState(params.get('space') || 'all');
  const spaces = useMemo(() => [...new Set(ALL.map((tile) => tile.slug))].map((s) => ({ slug: s, label: ALL.find((tile) => tile.slug === s).label })), [TILES, GALLERY_EXTRA]);
  const items = ALL.filter((tile) => (type === 'all' || tile.type === type) && (space === 'all' || tile.slug === space));

  return (
    <>
      <Seo title={t['seo.gallery.title']} description={t['seo.gallery.desc']}
        jsonLd={[breadcrumbLd(['Home', 'Gallery'])]} />
      <section className="bg-ink pb-24 pt-16 md:pt-24">
        <div className="container-site">
          <MaskedLines as="h1" className="font-serif text-4xl font-semibold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl"
            lines={[{ text: t['gl.h1a'], cls: 'text-white' }, { text: t['gl.h1b'], cls: 'italic text-gold' }]} />

          <FadeUp delay={0.3} className="mt-10">
            <div className="flex flex-wrap gap-2" role="group" aria-label={t['gl.filterType']}>
              {FILTERS.map((f) => (
                <button key={f.v} data-testid={`filter-type-${f.v}`} onClick={() => setType(f.v)}
                  className={`rounded-full px-5 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${type === f.v ? 'bg-gold text-ink' : 'border border-white/15 text-white/70 hover:border-gold hover:text-gold'}`}>
                  {f.l}
                </button>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label={t['gl.filterSpace']}>
              <button data-testid="filter-space-all" onClick={() => setSpace('all')}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${space === 'all' ? 'bg-white text-ink' : 'border border-white/15 text-white/60 hover:border-gold hover:text-gold'}`}>
                {t['gl.allSpaces']}
              </button>
              {spaces.map((s) => (
                <button key={s.slug} data-testid={`filter-space-${s.slug}`} onClick={() => setSpace(s.slug)}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${space === s.slug ? 'bg-white text-ink' : 'border border-white/15 text-white/60 hover:border-gold hover:text-gold'}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </FadeUp>

          {space !== 'all' && (
            <FadeUp data-testid="gallery-filtered-cta" className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-gold/30 bg-gold/5 p-6">
              <p className="text-sm text-white/85">{t['gl.loveA']} <strong className="text-gold">{spaces.find((s) => s.slug === space)?.label.toLowerCase()}</strong> {t['gl.loveB']}</p>
              <Link data-testid="gallery-filtered-quote-btn" to="/quote" className="gold-btn !py-2.5">{t['gl.getQuote']} <ArrowRight className="h-4 w-4" /></Link>
            </FadeUp>
          )}

          <div className="mt-12 grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4">
            {items.map((tile, i) => (
              <FadeUp key={tile.label + i} delay={(i % 4) * 0.05}>
                <figure data-testid={`gallery-item-${i}`} className="group relative overflow-hidden rounded-xl shadow-lg">
                  {tile.video ? (
                    <video src={tile.video} autoPlay muted loop playsInline preload="metadata"
                      aria-label={`${tile.label} — Reverence Services, Ottawa`}
                      className="photo-treat aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  ) : (
                    <img src={tile.img} alt={`${tile.label} — Reverence Services, Ottawa`} loading="lazy"
                      className="photo-treat aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  )}
                  <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 pb-3 pt-10">
                    <span className="text-sm font-bold text-white">{tile.label}</span>
                    <span className="block text-[11px] uppercase tracking-widest text-gold">{tile.type === 'interior' ? t['gl.interior'] : t['gl.exterior']}</span>
                  </figcaption>
                </figure>
              </FadeUp>
            ))}
            {items.length === 0 && <p data-testid="gallery-empty" className="col-span-full py-16 text-center text-silver">{t['gl.empty']}</p>}
          </div>

          <FadeUp className="mt-16 text-center">
            <Link data-testid="gallery-quote-cta" to="/quote" className="gold-btn text-base !px-10 !py-4">{t['gl.cta']} <ArrowRight className="h-5 w-5" /></Link>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
